import { useState, useEffect } from "react";
import { api } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Gift, Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminRewards = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cost_points: "",
    reward_type: "discount",
    value_cents: "",
    cap_per_user: "",
    is_active: true
  });

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await api.get("/rewards");
      setRewards(response.data);
    } catch (error) {
      toast.error("Failed to load rewards");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      cost_points: "",
      reward_type: "discount",
      value_cents: "",
      cap_per_user: "",
      is_active: true
    });
    setEditingReward(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      cost_points: reward.cost_points.toString(),
      reward_type: reward.reward_type,
      value_cents: reward.value_cents ? (reward.value_cents / 100).toString() : "",
      cap_per_user: reward.cap_per_user?.toString() || "",
      is_active: reward.is_active
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description || !formData.cost_points) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        cost_points: parseInt(formData.cost_points),
        reward_type: formData.reward_type,
        value_cents: formData.value_cents ? Math.round(parseFloat(formData.value_cents) * 100) : null,
        cap_per_user: formData.cap_per_user ? parseInt(formData.cap_per_user) : null,
        is_active: formData.is_active
      };

      if (editingReward) {
        await api.put(`/admin/rewards/${editingReward.id}`, payload);
        toast.success("Reward updated");
      } else {
        await api.post("/admin/rewards", payload);
        toast.success("Reward created");
      }

      setDialogOpen(false);
      resetForm();
      fetchRewards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reward");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/rewards/${id}`);
      toast.success("Reward deleted");
      fetchRewards();
    } catch (error) {
      toast.error("Failed to delete reward");
    } finally {
      setDeleting(null);
    }
  };

  const getRewardTypeLabel = (type) => {
    switch(type) {
      case 'discount': return 'Discount';
      case 'upgrade': return 'Upgrade';
      case 'free_service': return 'Free Service';
      default: return type;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Rewards Catalog">
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-32 rounded-xl animate-shimmer" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Rewards Catalog">
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-[#A0A0A0]">{rewards.length} rewards in catalog</p>
          <Button 
            onClick={openCreateDialog}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B]"
            data-testid="create-reward-btn"
          >
            <Plus size={18} className="mr-2" /> Add Reward
          </Button>
        </div>

        {/* Rewards List */}
        {rewards.length > 0 ? (
          <div className="space-y-4">
            {rewards.map((reward) => (
              <Card key={reward.id} className={`bg-[#131316] border-[#2A2A2F] ${!reward.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold text-lg">{reward.name}</h3>
                        <span className="badge badge-gold">{getRewardTypeLabel(reward.reward_type)}</span>
                        {!reward.is_active && <span className="badge badge-error">Inactive</span>}
                      </div>
                      <p className="text-[#A0A0A0] text-sm mb-3">{reward.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-[#D4AF37] font-semibold">
                          {reward.cost_points.toLocaleString()} miles
                        </span>
                        {reward.value_cents && (
                          <span className="text-[#4ADE80]">
                            Value: ${(reward.value_cents / 100).toFixed(2)}
                          </span>
                        )}
                        {reward.cap_per_user && (
                          <span className="text-[#6B6B6B]">
                            Cap: {reward.cap_per_user} per user
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => openEditDialog(reward)}
                        className="border-[#2A2A2F] text-[#A0A0A0] hover:text-white hover:border-[#D4AF37]"
                        data-testid={`edit-reward-${reward.id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => handleDelete(reward.id)}
                        disabled={deleting === reward.id}
                        className="border-[#2A2A2F] text-[#A0A0A0] hover:text-[#EF4444] hover:border-[#EF4444]"
                        data-testid={`delete-reward-${reward.id}`}
                      >
                        {deleting === reward.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Gift size={64} className="mx-auto mb-4 text-[#6B6B6B] opacity-50" />
            <h3 className="text-white text-xl font-semibold mb-2">No Rewards Yet</h3>
            <p className="text-[#A0A0A0] mb-4">Create your first reward to get started</p>
            <Button onClick={openCreateDialog} className="bg-[#D4AF37] text-[#0A0A0B]">
              <Plus size={18} className="mr-2" /> Add Reward
            </Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#131316] border-[#2A2A2F] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingReward ? 'Edit Reward' : 'Create Reward'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., $10 Off Next Ride"
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="reward-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A0A0A0]">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the reward..."
                  rows={3}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37] resize-none"
                  data-testid="reward-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Cost (Miles) *</Label>
                  <Input
                    type="number"
                    value={formData.cost_points}
                    onChange={(e) => setFormData({ ...formData, cost_points: e.target.value })}
                    placeholder="500"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                    data-testid="reward-cost-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Type</Label>
                  <Select
                    value={formData.reward_type}
                    onValueChange={(value) => setFormData({ ...formData, reward_type: value })}
                  >
                    <SelectTrigger className="bg-[#1A1A1E] border-[#2A2A2F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1E] border-[#2A2A2F]">
                      <SelectItem value="discount" className="text-white">Discount</SelectItem>
                      <SelectItem value="upgrade" className="text-white">Upgrade</SelectItem>
                      <SelectItem value="free_service" className="text-white">Free Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Value ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.value_cents}
                    onChange={(e) => setFormData({ ...formData, value_cents: e.target.value })}
                    placeholder="10.00"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                    data-testid="reward-value-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#A0A0A0]">Cap Per User</Label>
                  <Input
                    type="number"
                    value={formData.cap_per_user}
                    onChange={(e) => setFormData({ ...formData, cap_per_user: e.target.value })}
                    placeholder="No limit"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between py-3 border-t border-[#2A2A2F]">
                <div>
                  <p className="text-white font-medium">Active</p>
                  <p className="text-[#6B6B6B] text-sm">Visible to customers</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#2A2A2F] text-white hover:bg-[#1A1A1E]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#D4AF37] text-[#0A0A0B]"
                data-testid="save-reward-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingReward ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRewards;
